import { useEffect, useState } from 'react';
import { getBalances, getFinanceComposition, getFinanceNextMonth, getFinanceTodo } from '../../api/invoicing.api';
import {
  BalanceBucketsDto,
  FinanceCompositionDto,
  FinanceNextMonthDto,
  FinanceTodoItemDto
} from '../../common/interfaces/invoicing.interface';
import { parseMonthKey } from '../../utilities/helper/date.helper';

interface TodayArgs {
  kindergartenId: string;
  enabled: boolean;
  locationId?: string;
  groupId?: string;
  /** A group's to-do names who has no invoice for this month. */
  month?: string;
  /** Bumped to fetch again, after a payment or a price list was saved. */
  reloadKey?: number;
}

/**
 * What is about today rather than a month - to do, today's debts, next month - for the
 * kindergarten, a location or a group.
 */
export const useFinanceToday = ({ kindergartenId, enabled, locationId, groupId, month, reloadKey = 0 }: TodayArgs) => {
  const [todo, setTodo] = useState<FinanceTodoItemDto[] | null>(null);
  const [buckets, setBuckets] = useState<BalanceBucketsDto | null>(null);
  const [nextMonth, setNextMonth] = useState<FinanceNextMonthDto | null>(null);

  useEffect(() => {
    if (!kindergartenId || !enabled) return undefined;
    let cancelled = false;
    const scope = locationId ? { locationId } : groupId ? { groupId } : {};
    const period = groupId && month ? parseMonthKey(month) : null;
    getFinanceTodo(kindergartenId, { ...scope, ...(period ?? {}) })
      .then(result => !cancelled && setTodo(result.results ?? []))
      .catch(() => !cancelled && setTodo([]));
    // one row is enough: the cards' figures come with any page of the debts list
    getBalances(kindergartenId, { perPage: 1, bucket: 'all', ...(groupId ? { groupIds: [groupId] } : locationId ? { locationId } : {}) })
      .then(result => !cancelled && setBuckets(result.results?.buckets ?? null))
      .catch(() => undefined);
    // a group's page shows its price list in this place instead
    if (!groupId) {
      getFinanceNextMonth(kindergartenId, scope)
        .then(result => !cancelled && setNextMonth(result.results ?? null))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [kindergartenId, enabled, locationId, groupId, month, reloadKey]);

  return { todo, buckets, nextMonth };
};

/** What the chosen month was invoiced for, in the same scope. */
export const useFinanceComposition = ({ kindergartenId, enabled, locationId, groupId, month, reloadKey = 0 }: TodayArgs) => {
  const [composition, setComposition] = useState<FinanceCompositionDto | null>(null);
  useEffect(() => {
    const parsed = month ? parseMonthKey(month) : null;
    if (!kindergartenId || !enabled || !parsed) return undefined;
    let cancelled = false;
    getFinanceComposition(kindergartenId, { ...parsed, locationId, groupId })
      .then(result => !cancelled && setComposition(result.results ?? null))
      .catch(() => !cancelled && setComposition(null));
    return () => {
      cancelled = true;
    };
  }, [kindergartenId, enabled, locationId, groupId, month, reloadKey]);
  return composition;
};
